import {
  retailRateRequestStatusFromJSON,
  retailRateRequestStatusReasonFromJSON,
  retailRateRequestStatusReasonToJSON,
  retailRateRequestStatusToJSON,
} from '@classpass/protobuf-definitions/lib/web/models/partners/retail_rate_requests';

export const RETAIL_RATE_REQUEST_STATUS = {
  REQUESTED: 'REQUESTED',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
  APPROVED: 'APPROVED',
  CANCELLED: 'CANCELLED',
  MANUAL_HANDOFF: 'MANUAL_HANDOFF',
};

export const RETAIL_RATE_REQUEST_STATUS_REASON = {
  PROOF_UNTRUSTED: 'PROOF_UNTRUSTED',
  PARTNER_DECLINED: 'PARTNER_DECLINED',
  GENRE_DISALLOWED: 'GENRE_DISALLOWED',
  PARTNER_DISAGREEMENT: 'PARTNER_DISAGREEMENT',
  RATE_CHANGES_NEEDED: 'RATE_CHANGES_NEEDED',
  PROOF_CHANGES_NEEDED: 'PROOF_CHANGES_NEEDED',
};

const PENDING_STATUSES = new Set([
  RETAIL_RATE_REQUEST_STATUS.REQUESTED,
  RETAIL_RATE_REQUEST_STATUS.PENDING,
  RETAIL_RATE_REQUEST_STATUS.MANUAL_HANDOFF,
]);

// Rejections only ever carry these four reasons (the approval-only reasons never
// reach a rejected request) — a missing key returning null is expected, not a gap.
const REJECTED_REASON_TOOLTIP_KEYS = {
  [RETAIL_RATE_REQUEST_STATUS_REASON.PROOF_UNTRUSTED]: 'rejectedProofUntrustedTooltip',
  [RETAIL_RATE_REQUEST_STATUS_REASON.PARTNER_DECLINED]: 'rejectedPartnerDeclinedTooltip',
  [RETAIL_RATE_REQUEST_STATUS_REASON.GENRE_DISALLOWED]: 'rejectedGenreTooltip',
  [RETAIL_RATE_REQUEST_STATUS_REASON.PARTNER_DISAGREEMENT]: 'rejectedDisagreementTooltip',
};

const stripStatusPrefix = value =>
  value?.startsWith('RETAIL_RATE_REQUEST_STATUS_') ? value.replace('RETAIL_RATE_REQUEST_STATUS_', '') : value;

const stripStatusReasonPrefix = value =>
  value?.startsWith('RETAIL_RATE_REQUEST_STATUS_REASON_')
    ? value.replace('RETAIL_RATE_REQUEST_STATUS_REASON_', '')
    : value;

export function normalizeRetailRateRequestStatus(status) {
  if (status === null || status === undefined || status === '') {
    return null;
  }

  if (typeof status === 'string' && !status.startsWith('RETAIL_RATE_REQUEST_STATUS_') && Number.isNaN(Number(status))) {
    return status;
  }

  return stripStatusPrefix(retailRateRequestStatusToJSON(retailRateRequestStatusFromJSON(status)));
}

export function normalizeRetailRateRequestStatusReason(statusReason) {
  if (statusReason === null || statusReason === undefined || statusReason === '') {
    return null;
  }

  if (
    typeof statusReason === 'string' &&
    !statusReason.startsWith('RETAIL_RATE_REQUEST_STATUS_REASON_') &&
    Number.isNaN(Number(statusReason))
  ) {
    return statusReason;
  }

  return stripStatusReasonPrefix(
    retailRateRequestStatusReasonToJSON(retailRateRequestStatusReasonFromJSON(statusReason))
  );
}

export function getRetailRateRequestStatus(retailRateRequest) {
  if (retailRateRequest == null) {
    return null;
  }

  return normalizeRetailRateRequestStatus(retailRateRequest.status);
}

export function getRetailRateRequestStatusReason(retailRateRequest) {
  if (retailRateRequest == null) {
    return null;
  }

  const statusReason = retailRateRequest.statusReason ?? retailRateRequest.status_reason;

  return normalizeRetailRateRequestStatusReason(statusReason);
}

export function isRetailRateRequestPending(retailRateRequest = {}) {
  return PENDING_STATUSES.has(getRetailRateRequestStatus(retailRateRequest));
}

// Before the proof flow, support resolved MANUAL_HANDOFF requests by pricing the
// class in admin without ever closing the request. A live price means that
// handoff is done, so these must not read as still awaiting review.
export function isManualHandoffAlreadyPriced(retailRateRequest, hasPrice) {
  return !!hasPrice && getRetailRateRequestStatus(retailRateRequest) === RETAIL_RATE_REQUEST_STATUS.MANUAL_HANDOFF;
}

export function isRetailRateRequestRejected(retailRateRequest = {}) {
  return getRetailRateRequestStatus(retailRateRequest) === RETAIL_RATE_REQUEST_STATUS.REJECTED;
}

export function getRejectedRetailRateRequestTooltipKey(retailRateRequest = {}) {
  const statusReason = getRetailRateRequestStatusReason(retailRateRequest);

  return REJECTED_REASON_TOOLTIP_KEYS[statusReason] ?? null;
}
